import { type RefObject } from 'react';
import type { ChinBotSnapshot } from '@/lib/chin-bot-engine';
import type { StatusLevel, SymbolPlayConfig } from '@/hooks/useChinWorkspacePersistence';
import type { OrderLoopStatus } from '@/lib/chin-order-executor';
import type { OffEngineStatus } from '@/lib/chin-off-engine';
import type { RiskEvaluation } from '@/lib/chin-risk-controller';
import PlayCell from '@/pages/strategies/components/PlayCell';
import { cn, CHIN_TIMEFRAME, STATUS_OPTIONS, type SymbolEntry, type SymbolList } from './types';

interface ChinSymbolTableProps {
  selectedList: SymbolList;
  displaySymbols: SymbolEntry[];
  statusMap: Record<number, StatusLevel>;
  openStatusDropdown: number | null;
  onToggleStatusDropdown: (symbolId: number | null) => void;
  statusDropdownRef: RefObject<HTMLDivElement | null>;
  onStatusChange: (symbolId: number, level: StatusLevel) => void;
  playMap: Record<number, boolean>;
  symbolConfigs: Record<number, SymbolPlayConfig>;
  evaluation: RiskEvaluation | null;
  activeSymbolIds: number[];
  engineState: ChinBotSnapshot | null;
  orderStatuses: Record<number, OrderLoopStatus>;
  offEngineStatuses: Record<number, OffEngineStatus>;
  connected: boolean;
  isEngineRunning: boolean;
  riskLoading: boolean;
  onRefreshPositions: () => void;
  onPriceChange: (symbolId: number, val: string) => void;
  onMaxChange: (symbolId: number, val: string) => void;
  onVolumeChange: (symbolId: number, val: string) => void;
  onDirectionChange: (symbolId: number, dir: 'buy' | 'sell' | null) => void;
  onOrderDirectionChange: (symbolId: number, dir: 'buy' | 'sell' | null) => void;
  onResetSymbol: (symbolId: number) => void;
  onBoxXChange: (symbolId: number, val: string) => void;
  onBoxAlertToggle: (symbolId: number) => void;
  onOpenNote: (symbolId: number) => void;
}

export default function ChinSymbolTable({
  selectedList,
  displaySymbols,
  statusMap,
  openStatusDropdown,
  onToggleStatusDropdown,
  statusDropdownRef,
  onStatusChange,
  playMap,
  symbolConfigs,
  evaluation,
  activeSymbolIds,
  engineState,
  orderStatuses,
  offEngineStatuses,
  connected,
  isEngineRunning,
  riskLoading,
  onRefreshPositions,
  onPriceChange,
  onMaxChange,
  onVolumeChange,
  onDirectionChange,
  onOrderDirectionChange,
  onResetSymbol,
  onBoxXChange,
  onBoxAlertToggle,
  onOpenNote,
}: ChinSymbolTableProps) {
  return (
    <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      {/* Table header */}
      <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-accent-500/10 flex items-center justify-center">
            <i className="ri-list-check-2 text-accent-400 text-sm"></i>
          </div>
          <div>
            <span className="text-sm font-heading font-semibold text-foreground-900">{selectedList.name}</span>
            <span className="text-[10px] text-foreground-400 ml-2">{displaySymbols.length} symbols</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1 text-[10px] text-accent-400 bg-accent-500/10 px-2 py-0.5 rounded-full font-medium">
            <i className="ri-time-line"></i>
            {CHIN_TIMEFRAME}
          </span>
          {connected && (
            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              tick-by-tick
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">

        {/* ─── Risk Control Status Banner ─── */}
        {evaluation && evaluation.symbolsWithPositions.length > 0 && (
          <div className="px-4 py-2 bg-amber-500/5 border-b border-amber-500/10 flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-shield-flash-line text-amber-400 text-[10px]"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-amber-400">
                Single Symbol Mode — {evaluation.symbolsWithPositions.length} position(s) active
              </p>
              <p className="text-[9px] text-foreground-500 mt-0.5">
                Other symbols are blocked. Only 1 symbol can trade at a time. Close existing positions to trade another symbol.
              </p>
            </div>
            <button
              type="button"
              onClick={onRefreshPositions}
              disabled={riskLoading}
              className="px-2 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 rounded text-[9px] font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className={`${riskLoading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} mr-1`}></i>
              Refresh
            </button>
          </div>
        )}

        {evaluation && !evaluation.symbolsWithPositions.length && (
          (() => {
            const blockedSymbols = activeSymbolIds.filter(
              (sid) => {
                const blockedBy = evaluation?.checks[sid]?.blockedBy;
                return blockedBy !== 'none' && blockedBy !== undefined && blockedBy !== 'spread';
              }
            );
            if (blockedSymbols.length === 0) return null;
            return (
              <div className="px-4 py-2 bg-red-500/5 border-b border-red-500/10 flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-red-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-close-circle-line text-red-400 text-[10px]"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-red-400">
                    Orders Blocked — {blockedSymbols.length} symbol(s) affected
                  </p>
                  <p className="text-[9px] text-foreground-500 mt-0.5">
                    {blockedSymbols.map((sid) => {
                      const chk = evaluation?.checks[sid];
                      const name = engineState?.symbols[sid]?.symbolName || `#${sid}`;
                      return `${name}: ${chk?.reason || 'unknown'}`;
                    }).join(' | ')}
                  </p>
                </div>
              </div>
            );
          })()
        )}

        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-background-200/70 bg-background-100">
              <th className="sticky left-0 z-10 bg-background-100 text-left px-4 py-3 text-[11px] font-semibold text-foreground-600 uppercase tracking-wide border-r border-background-200/70 w-[160px]">
                Symbol
              </th>
              <th className="text-center px-4 py-3 text-[11px] font-semibold text-foreground-600 uppercase tracking-wide w-[140px]">
                Status
              </th>
              <th className="text-center px-3 py-3 text-[11px] font-semibold text-foreground-600 uppercase tracking-wide w-[130px]">
                Box
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold text-foreground-600 uppercase tracking-wide min-w-[290px]">
                Play
              </th>
            </tr>
          </thead>
          <tbody>
            {displaySymbols.map((sym, idx) => {
              const currentStatus = statusMap[sym.symbolId] || 'null';
              const isPlaying = playMap[sym.symbolId] || false;
              const isStatusOpen = openStatusDropdown === sym.symbolId;
              const config = symbolConfigs[sym.symbolId] || { conditionPrice: '', maxOrders: '' };
              const boxAlertOn = config.boxAlertOn !== false;
              const hasNote = Boolean((symbolConfigs[sym.symbolId]?.note || '').trim());

              return (
                <tr
                  key={sym.symbolId}
                  className={cn(
                    'border-b border-background-200/50 transition-colors group',
                    idx % 2 === 0 ? 'bg-background-50' : 'bg-background-50/50',
                    'hover:bg-background-100/70',
                    isPlaying && 'bg-emerald-500/5'
                  )}
                >
                  {/* Symbol name column */}
                  <td className="sticky left-0 z-10 bg-inherit px-4 py-3 border-r border-background-200/70">
                    <div className="flex items-center gap-2.5">
                      <span className="w-6 h-6 rounded-md bg-background-200/70 flex items-center justify-center text-[10px] font-mono font-bold text-foreground-500 shrink-0">
                        {idx + 1}
                      </span>
                      <span className="font-mono font-semibold text-foreground-900 text-sm">
                        {sym.symbolName}
                      </span>
                      <button
                        type="button"
                        onClick={() => onOpenNote(sym.symbolId)}
                        title={hasNote ? 'Xem / sửa ghi chú' : 'Thêm ghi chú'}
                        className={cn(
                          'w-6 h-6 flex items-center justify-center rounded-md transition-colors cursor-pointer shrink-0',
                          hasNote
                            ? 'text-accent-400 bg-accent-500/10 hover:bg-accent-500/20'
                            : 'text-foreground-400 hover:bg-background-200/70 hover:text-foreground-600'
                        )}
                      >
                        <i className={hasNote ? 'ri-information-fill text-sm' : 'ri-information-line text-sm'}></i>
                      </button>
                    </div>
                  </td>

                  {/* Status dropdown */}
                  <td className="text-center px-4 py-3">
                    <div className="relative inline-block" ref={isStatusOpen ? statusDropdownRef : undefined}>
                      <button
                        type="button"
                        onClick={() => onToggleStatusDropdown(isStatusOpen ? null : sym.symbolId)}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap border',
                          currentStatus !== 'null'
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                            : 'bg-background-100 border-background-300/60 text-foreground-400 hover:border-background-400/60 hover:text-foreground-600'
                        )}
                      >
                        <span className={cn(
                          'w-1.5 h-1.5 rounded-full',
                          currentStatus !== 'null' && 'bg-emerald-400',
                          currentStatus === 'null' && 'bg-foreground-300'
                        )}></span>
                        {STATUS_OPTIONS.find((o) => o.value === currentStatus)?.shortLabel || '—'}
                        <i className="ri-arrow-down-s-line text-[10px] opacity-60"></i>
                      </button>

                      {isStatusOpen && (
                        <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-40 overflow-hidden min-w-[300px] max-w-[360px]">
                          {STATUS_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => onStatusChange(sym.symbolId, opt.value)}
                              className={cn(
                                'w-full text-left px-3 py-2 text-[11px] font-medium transition-colors cursor-pointer flex items-center gap-2',
                                currentStatus === opt.value
                                  ? 'bg-primary-500/10 text-primary-600'
                                  : 'hover:bg-background-100 text-foreground-600'
                              )}
                            >
                              <span className={cn(
                                'w-1.5 h-1.5 rounded-full shrink-0',
                                opt.value !== 'null' && 'bg-emerald-400',
                                opt.value === 'null' && 'bg-foreground-300'
                              )}></span>
                              {opt.label}
                              {currentStatus === opt.value && (
                                <i className="ri-check-line text-primary-500 ml-auto text-xs"></i>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Box column */}
                  <td className="px-3 py-2 align-middle">
                    <div className="flex flex-col items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => onBoxAlertToggle(sym.symbolId)}
                        className={cn(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap border',
                          boxAlertOn
                            ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                            : 'bg-background-100 border-background-300/60 text-foreground-400 hover:border-foreground-400/60'
                        )}
                        title={boxAlertOn
                          ? 'Box Telegram alerts ON — click to mute (engine keeps running)'
                          : 'Box Telegram alerts MUTED — engine still detecting, no message sent'}
                      >
                        <span className={cn('w-1.5 h-1.5 rounded-full', boxAlertOn ? 'bg-amber-400' : 'bg-foreground-300')}></span>
                        {boxAlertOn ? 'ON' : 'OFF'}
                      </button>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={config.boxX || ''}
                        placeholder="10"
                        onChange={(e) => onBoxXChange(sym.symbolId, e.target.value)}
                        title="Box threshold (pips) — default 10"
                        className="w-16 px-2 py-1.5 text-center text-xs rounded-lg border border-background-300/60 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20"
                      />
                    </div>
                  </td>

                  {/* Play column */}
                  <td className="px-3 py-2 align-top">
                    <PlayCell
                      symbolId={sym.symbolId}
                      conditionPrice={config.conditionPrice}
                      maxOrders={config.maxOrders}
                      direction={config.direction || null}
                      orderDirection={config.orderDirection || null}
                      orderVolume={config.orderVolume || ''}
                      onVolumeChange={onVolumeChange}
                      onReset={onResetSymbol}
                      onPriceChange={onPriceChange}
                      onMaxChange={onMaxChange}
                      onDirectionChange={onDirectionChange}
                      onOrderDirectionChange={onOrderDirectionChange}
                      riskBlocked={evaluation?.checks[sym.symbolId]?.allowed === false && evaluation?.checks[sym.symbolId]?.blockedBy !== 'spread'}
                      riskReason={evaluation?.checks[sym.symbolId]?.reason || ''}
                      spreadPips={evaluation ? (
                        (() => {
                          // spread info is embedded in the check
                          const chk = evaluation.checks[sym.symbolId];
                          if (!chk) return null;
                          // extract spread from engine state directly
                          const buf = engineState?.symbols[sym.symbolId];
                          if (!buf || buf.currentBid === null || buf.currentAsk === null) return null;
                          const raw = buf.currentAsk - buf.currentBid;
                          if (raw <= 0) return 0;
                          // approximate pip size from digits
                          const pipSize = buf.digits === 3 ? 0.01 : 0.0001;
                          return raw / pipSize;
                        })()
                      ) : null}
                      positionCount={evaluation?.positionCountBySymbol[sym.symbolId] || 0}
                      orderStatus={orderStatuses[sym.symbolId] || null}
                      offEngineStatus={offEngineStatuses[sym.symbolId] || null}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Table footer */}
      <div className="px-4 py-2.5 border-t border-background-200/70 bg-background-100 flex items-center justify-between">
        <span className="text-[10px] text-foreground-400">
          {displaySymbols.length} symbols in {selectedList.name} &middot; {CHIN_TIMEFRAME} timeframe
        </span>
        <span className="text-[10px] text-foreground-400">
          {connected ? 'Streaming tick-by-tick' : isEngineRunning ? 'Connecting...' : 'Ready'}
        </span>
      </div>
    </div>
  );
}